import React, { useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import { IconButton, Box, SxProps, Typography } from "@mui/material";
import { Theme } from "@mui/material/styles";
import styled from "@mui/material/styles/styled";

import GoogleButton from "@app/common/components/GoogleButton";
import { PROVIDERS_BY_ID } from "@app/common/constants/providers";
import type { Provider, ProviderId } from "@app/common/types/providerTypes";
import Config from "@app/config";
import { lightOrDark } from "@app/embedded/theme/utils";

import { Row } from "@rui/Stack";

interface CommonProviderButtonProps {
  className?: string;
  disabled?: boolean;
  icon?: React.ReactElement;
  children?: React.ReactNode;
  label?: string;
  loading?: boolean;
  sx?: SxProps<Theme>;
  variant?: "icon" | "button";
  type?: "submit" | "button";
  endIcon?: React.ReactNode;
}

interface ProviderButtonWithIdProps extends CommonProviderButtonProps {
  providerId: ProviderId;
  onClick?: (
    e: React.MouseEvent,
    providerId: ProviderId,
    provider: Provider
  ) => void;
}

interface ProviderButtonWithProviderProps<T extends Provider>
  extends CommonProviderButtonProps {
  provider: T;
  onClick?: (e: React.MouseEvent, providerId: T["id"], provider: T) => void;
}

export type ProviderButtonProps<T extends Provider = any> = T extends Provider
  ? ProviderButtonWithProviderProps<T>
  : ProviderButtonWithIdProps;

const ProviderIcon = ({
  icon,
  width,
}: {
  icon: ProviderButtonProps["icon"];
  width: React.CSSProperties["width"];
}) => {
  return (
    <Box
      sx={{
        width,
        minWidth: width,
        "& img": { width: "100%", display: "block" },
      }}
    >
      {icon}
    </Box>
  );
};

const StyledLoadingButton = styled(LoadingButton)(({ theme }) => ({
  color: theme.palette.shades.neutral[800],
  textTransform: "none",
  padding: "16px",
  borderWidth: "1px",
  borderColor: theme.palette.shades.neutral[100],
  justifyContent: "space-between",
  "&:hover": {
    borderWidth: "2px",
    padding: "15px",
  },
  // button may be disabled if the provider is already connected, however this isn't actually
  // a case where we want the button to *appear* disabled
  "&.Mui-disabled": {
    borderColor: "transparent",
  },
  // to appear disabled, the `disabled` class needs to be applied (e.g., provider is disabled for workspace)
  "&.disabled": {
    backgroundColor: theme.palette.shades.neutral[50],
    opacity: 0.8,
    "& img": {
      filter: "grayscale(1)",
    },
  },
}));

const ButtonVariant = <T extends Provider>({
  className,
  disabled,
  onClick,
  children,
  loading,
  provider: providerConfig,
  icon,
  sx,
  type = "submit",
  endIcon,
}: ProviderButtonWithProviderProps<T>) => {
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const showSpinner = loading || (loading == null && isInitialLoading);

  const label = children
    ? children
    : `Continue with ${providerConfig.displayName}`;

  const providerIcon = icon ? (
    icon
  ) : (
    <img
      src={providerConfig.icon}
      alt={`Continue with a ${providerConfig.displayName} account`}
    />
  );

  return (
    <StyledLoadingButton
      className={className}
      variant="outlined"
      sx={sx}
      onClick={(e: React.MouseEvent) => {
        setIsInitialLoading(true);
        onClick?.(e, providerConfig.id, providerConfig);
      }}
      type={type}
      loading={showSpinner}
      disabled={disabled}
    >
      <Row variant="center" spacing={1.5} width="100%">
        <ProviderIcon icon={providerIcon} width="24px" />
        <Typography
          variant="bodyPrimaryReg"
          color="shades.neutral.800"
          className="provider-button__label"
          sx={(theme) => ({
            color: lightOrDark("inherit", "#eeeeee")(theme),
          })}
        >
          {label}
        </Typography>
        {endIcon}
      </Row>
    </StyledLoadingButton>
  );
};

const IconButtonVariant = <T extends Provider>({
  className,
  disabled,
  onClick,
  provider: providerConfig,
  icon,
  label,
  sx,
  type = "submit",
}: ProviderButtonWithProviderProps<T>) => {
  const providerLabel = label
    ? label
    : `Continue with ${providerConfig.displayName}`;

  const providerIcon = icon ?? (
    <img
      src={providerConfig.icon}
      alt={`Continue with a ${providerConfig.displayName} account`}
    />
  );

  return (
    <IconButton
      className={className}
      sx={{
        width: "60px",
        height: "60px",
        border: "1px solid #E0E0E0",
        borderRadius: "20px",
        boxShadow: (theme) => theme.shadows[2],
        ...sx,
      }}
      onClick={(e: React.MouseEvent) =>
        onClick?.(e, providerConfig.id, providerConfig)
      }
      type={type}
      disabled={disabled}
      aria-label={providerLabel}
    >
      <ProviderIcon icon={providerIcon} width="100%" />
    </IconButton>
  );
};

const ProviderButton = ({
  variant = "button",
  ...props
}: ProviderButtonProps) => {
  const provider =
    "providerId" in props ? PROVIDERS_BY_ID[props.providerId] : props.provider;

  if (Config.flags.googleReview && variant !== "icon") {
    return (
      <GoogleButton
        disabled={props.disabled}
        startIcon={props.icon}
        endIcon={props.endIcon}
        onClick={(e) => props.onClick?.(e, provider.id, provider)}
      >
        {props.children}
      </GoogleButton>
    );
  }

  if (variant === "icon") {
    return <IconButtonVariant {...props} provider={provider} />;
  }

  return <ButtonVariant {...props} provider={provider} />;
};

export default ProviderButton;
